import * as React from "react";
import { Alert, AlertColor, Box, Stack, TextField, TextFieldProps } from "@mui/material";
import CclDrawer from "../../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../../components/common/cclDrawer/cclDrawerActionBar";
import CclLoadingButton from "../../../../components/common/cclButtons/cclLoadingButton";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import { DatePicker } from "@mui/x-date-pickers";
import { SessionDetailType } from "../../../../services/types/sessionDetailTypes";
import { useGetSessionSetUpByEventKeyQuery } from "../../../../services/cclTokenedSessionApi";
import { useLazyGetCclUserAccountDetailQuery } from "../../../../services/cclTokenedGrandCentralApi";
import { validateEmail } from "../../../../utilities/generalUtilities";
import { useRegisterPartnerSessionParticipantMutation } from "../../../../services/cclTokenedEnterpriseParticipantApi";
import {
    PartnerSessionRegistration,
    RegisterPartnerSessionParticipantsRequest,
} from "../../../../services/types/enterpriseParticipantApiTypes";
import { CreateEnterpriseUserRequest } from "../../../../services/types/enterpriseUserApiTypes";
import { useCreateNewEnterpriseUserMutation } from "../../../../services/cclTokenedEnterpriseUserApi";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { AccessEventRegistrationDetails } from "../../../../services/types/accessEventTypes";
import { getLocalIsoDateWithNoTime } from "../../../../utilities/dateHelpers";

interface RegisterPartnerParticipantDrawerProps {
    open: boolean;
    session: SessionDetailType;
    onClose: () => void;
    showSnackbar: (message: string, severity: AlertColor) => void;
}

const searchInputSx = {
    flexGrow: 1,
};

const RegisterPartnerParticipantDrawer: React.FC<RegisterPartnerParticipantDrawerProps> = (
    props,
) => {
    const { logEvent } = useLogAccessEvent();
    const { data: sessionSetupData, isSuccess } = useGetSessionSetUpByEventKeyQuery(
        props.session.sessionKey.toString(),
        {
            skip: !props.session.sessionKey || props.session.sessionKey <= 0,
        },
    );

    const [register] = useRegisterPartnerSessionParticipantMutation();
    const [getUserData] = useLazyGetCclUserAccountDetailQuery();
    const [createUser] = useCreateNewEnterpriseUserMutation();

    const [completionDate, setCompletionDate] = React.useState<Date | null>(null);
    const [regInProgress, setRegInProgress] = React.useState<boolean>(false);

    const [email, setEmail] = React.useState<string>("");
    const [firstName, setFirstName] = React.useState<string>("");
    const [lastName, setLastName] = React.useState<string>("");
    const [imKey, setImKey] = React.useState<number | null>(null);
    const [lockNames, setLockNames] = React.useState<boolean>(false);
    const emailDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
        return () => {
            if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
        };
    }, []);

    React.useEffect(() => {
        if (isSuccess && sessionSetupData) {
            setCompletionDate(sessionSetupData.sessionProgramData.surveyCompletionDate);
        }
    }, [isSuccess, sessionSetupData]);

    const getTodayStart = (): Date => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    const validateDateFields = (): string[] => {
        const messages: string[] = [];
        const today = getTodayStart();

        const completionDateObj = completionDate ? new Date(completionDate) : null;
        const normalizedCompletionDate =
            completionDateObj && !isNaN(completionDateObj.getTime())
                ? new Date(completionDateObj.toDateString())
                : null;

        const sessionEndDateObj = sessionSetupData?.sessionProgramData.endDate
            ? new Date(sessionSetupData.sessionProgramData.endDate)
            : null;
        const normalizedSessionEndDate =
            sessionEndDateObj && !isNaN(sessionEndDateObj.getTime())
                ? new Date(sessionEndDateObj.toDateString())
                : null;

        if (normalizedCompletionDate) {
            if (
                normalizedCompletionDate < today ||
                (normalizedSessionEndDate && normalizedCompletionDate >= normalizedSessionEndDate)
            ) {
                messages.push(
                    "Assessment Completion must be before Session End Date and in the future",
                );
            }
        }

        return messages;
    };

    const validationMessages = React.useMemo(
        () => validateDateFields(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [completionDate, sessionSetupData?.sessionProgramData.endDate],
    );

    const validateMandatoryFields = (): boolean => {
        if (!email || email.trim() === "") return false;
        if (!firstName || firstName.trim() === "") return false;
        if (!lastName || lastName.trim() === "") return false;
        if (!completionDate) return false;
        return true;
    };

    const clearFields = () => {
        setEmail("");
        setFirstName("");
        setLastName("");
        setImKey(null);
        setLockNames(false);
        setCompletionDate(sessionSetupData?.sessionProgramData.surveyCompletionDate || null);
    };

    const registerNewParticipant = () => {
        if (completionDate == null) return;
        setRegInProgress(true);
        if (imKey == null || imKey <= 0) {
            const newUserRequest: CreateEnterpriseUserRequest = {
                email: email,
                firstName: firstName,
                lastName: lastName,
            };
            createUser(newUserRequest)
                .unwrap()
                .then((res) => {
                    registerParticipant(res.imKey, completionDate);
                })
                .catch(() => {
                    props.showSnackbar("Error creating new user", "error");
                    setRegInProgress(false);
                });
        } else {
            registerParticipant(imKey, completionDate);
        }
    };

    const registerParticipant = (incomingImKey: number, realCompletionDate: Date) => {
        const newReg: PartnerSessionRegistration = {
            imKey: incomingImKey,
            surveyCompletionDate: getLocalIsoDateWithNoTime(realCompletionDate),
        };

        const request: RegisterPartnerSessionParticipantsRequest = {
            esKey: props.session.sessionKey,
            newRegistrations: [newReg],
        };

        register(request)
            .unwrap()
            .then((newParticipants) => {
                if (newParticipants.length > 0) {
                    const evtData: AccessEventRegistrationDetails = {
                        esiKey: newParticipants[0].esiKey.toString(),
                        imKey: incomingImKey.toString(),
                        email: newParticipants[0].emailAddress,
                    };
                    logEvent("ParticipantRegistered", evtData);
                    props.showSnackbar("Participant Registered", "success");
                    setRegInProgress(false);
                    clearFields();
                } else {
                    props.showSnackbar("Register Participant Failed", "error");
                    setRegInProgress(false);
                }
            })
            .catch(() => {
                props.showSnackbar("Register Participant Failed", "error");
                setRegInProgress(false);
            });
    };

    const onEmailChange = (newEmail: string) => {
        setEmail(newEmail);
        if (lockNames) {
            setFirstName("");
            setLastName("");
            setImKey(null);
            setLockNames(false);
        }
        if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
        if (!validateEmail(newEmail)) return;
        emailDebounceRef.current = setTimeout(() => {
            getUserData(newEmail)
                .unwrap()
                .then((res) => {
                    if (res.length === 0) return;
                    setFirstName(res[0].firstName);
                    setLastName(res[0].lastName);
                    setImKey(res[0].imKey);
                    setLockNames(true);
                })
                .catch(() => {});
        }, 400);
    };

    return (
        <CclDrawer
            title={"Register Participant"}
            open={props.open}
            onClose={props.onClose}
            width="standard"
        >
            <Stack spacing={3} sx={{ overflow: "hidden", pt: 2 }}>
                <TextField
                    size="small"
                    fullWidth
                    label="Email"
                    value={email}
                    onChange={(e) => onEmailChange(e.target.value)}
                    sx={searchInputSx}
                />

                <TextField
                    size="small"
                    fullWidth
                    label="First Name"
                    value={firstName}
                    disabled={lockNames}
                    onChange={(e) => setFirstName(e.target.value)}
                    sx={searchInputSx}
                />

                <TextField
                    size="small"
                    fullWidth
                    label="Last Name"
                    value={lastName}
                    disabled={lockNames}
                    onChange={(e) => setLastName(e.target.value)}
                    sx={searchInputSx}
                />

                <DatePicker
                    label={"Assessment Completion"}
                    value={completionDate}
                    minDate={getTodayStart()}
                    maxDate={
                        sessionSetupData?.sessionProgramData.endDate
                            ? (() => {
                                  const maxDate = new Date(
                                      sessionSetupData.sessionProgramData.endDate,
                                  );
                                  maxDate.setDate(maxDate.getDate() - 1);
                                  return maxDate;
                              })()
                            : undefined
                    }
                    renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                        params.fullWidth = true;
                        return (
                            <TextField
                                {...params}
                                size="small"
                                onKeyDown={() => {}}
                                sx={searchInputSx}
                            />
                        );
                    }}
                    onChange={(e) => setCompletionDate(e)}
                />

                {validationMessages.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        {validationMessages.map((message, index) => (
                            <Alert key={index} severity="info" sx={{ mb: 1 }}>
                                {message}
                            </Alert>
                        ))}
                    </Box>
                )}
            </Stack>
            <CclDrawerActionBar>
                <CclLoadingButton
                    mode={"primary"}
                    onClick={registerNewParticipant}
                    loading={regInProgress}
                    disabled={!validateMandatoryFields() || validationMessages.length > 0}
                >
                    Register
                </CclLoadingButton>
                <CclButton
                    sx={{ float: "right", mr: 4 }}
                    mode={"secondary"}
                    onClick={props.onClose}
                >
                    Cancel
                </CclButton>
            </CclDrawerActionBar>
        </CclDrawer>
    );
};

export default RegisterPartnerParticipantDrawer;
